/**
 * Design System Admin Page
 * Interactive viewer for the Camino Design System with Storybook-style navigation
 */

import React, { useState } from "react";
import {
  Box,
  Container,
  Badge,
  SimpleGrid,
  IconButton,
  useColorMode,
  useColorModeValue,
  Heading,
  Text,
  Grid,
  Card,
  CardBody,
} from "@chakra-ui/react";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";

// Import our design system
import CDS from "design-system";

// Import viewer components (we'll create these)
import { FoundationViewer } from "./FoundationViewer";
import { PrimitivesViewer } from "./PrimitivesViewer";
import { ComponentsViewer } from "./ComponentsViewer";
import { LayoutViewer } from "./LayoutViewer";
import { PatternsViewer } from "./PatternsViewer";

interface DesignSystemSection {
  id: string;
  name: string;
  description: string;
  items: number; // Number of items in this section
  component: React.ComponentType;
}

const sections: DesignSystemSection[] = [
  {
    id: "foundation",
    name: "Foundation",
    description: "Core design tokens: colors, spacing, typography, shadows, and radius",
    items: 5,
    component: FoundationViewer,
  },
  {
    id: "primitives",
    name: "Primitives",
    description: "Basic UI elements: text, buttons, inputs, icons, and images",
    items: 10,
    component: PrimitivesViewer,
  },
  {
    id: "components",
    name: "Components",
    description: "Complex UI components: cards, modals, tables, navigation, and forms",
    items: 5,
    component: ComponentsViewer,
  },
  {
    id: "layout",
    name: "Layout",
    description: "Structural components: stack, grid, container, and section",
    items: 4,
    component: LayoutViewer,
  },
  {
    id: "patterns",
    name: "Patterns",
    description: "Complex patterns: page templates and workflows",
    items: 2,
    component: PatternsViewer,
  },
];

function DesignSystemPage() {
  const [selectedSection, setSelectedSection] = useState<string>("foundation");
  const [, setSelectedItem] = useState<string | null>(null);

  const { colorMode, toggleColorMode } = useColorMode();
  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headingColor = useColorModeValue("gray.900", "white");

  const currentSection = sections.find((s) => s.id === selectedSection);
  const CurrentViewer = currentSection?.component;

  return (
    <Box minH="100vh" bg={bg}>
      {/* Header */}
      <Box bg={cardBg} borderBottom="1px" borderColor={borderColor}>
        <Container maxW="8xl" py={6}>
          <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small}>
            <CDS.Stack
              direction={CDS.Stack.direction.horizontal}
              justify={CDS.Stack.justify.between}
              w={CDS.Size.full}
              align={CDS.Stack.align.center}
            >
              <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
                <Heading size="lg" color={headingColor}>
                  Camino Design System
                </Heading>
                <Badge colorScheme="green" variant="subtle">
                  v0.0.1
                </Badge>
              </CDS.Stack>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
                <CDS.Text variant={CDS.Text.variant.bodySmall} color={CDS.Color.text.secondary}>
                  {colorMode === "light" ? "Light" : "Dark"}
                  {" Mode"}
                </CDS.Text>
                <IconButton
                  aria-label={`Switch to ${colorMode === "light" ? "dark" : "light"} mode`}
                  icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
                  onClick={toggleColorMode}
                  variant="ghost"
                  size="sm"
                />
              </CDS.Stack>
            </CDS.Stack>
            <CDS.Text variant={CDS.Text.variant.bodyLarge} color={CDS.Color.text.secondary}>
              Interactive documentation and component library - See colors in both light and dark modes!
            </CDS.Text>
          </CDS.Stack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="8xl" py={8}>
        <Grid templateColumns="300px 1fr" gap={8} h="calc(100vh - 200px)">
          {/* Sidebar Navigation */}
          <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody>
                <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.small}>
                  <CDS.Text variant={CDS.Text.variant.labelSmall} color={CDS.Color.text.secondary} style={{ textTransform: "uppercase" }}>
                    Sections
                  </CDS.Text>
                  <CDS.SectionSelector
                    sections={sections.map((section) => ({
                      id: section.id,
                      name: section.name,
                      description: section.description,
                      items: section.items,
                    }))}
                    selectedSection={selectedSection}
                    onSectionChange={(sectionId) => {
                      setSelectedSection(sectionId);
                      setSelectedItem(null);
                    }}
                    showItemCount
                  />
                </CDS.Stack>
              </CardBody>
            </Card>

            {/* Quick Stats */}
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody>
                <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.small}>
                  <CDS.Text variant={CDS.Text.variant.labelSmall} color={CDS.Color.text.secondary} style={{ textTransform: "uppercase" }}>
                    Overview
                  </CDS.Text>
                  <SimpleGrid columns={1} spacing={2}>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" color="blue.400">
                        {sections.reduce((acc, s) => acc + s.items, 0)}
                      </Text>
                      <CDS.Text variant={CDS.Text.variant.bodySmall} color={CDS.Color.text.secondary}>Total Components</CDS.Text>
                    </Box>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" color="green.400">
                        100%
                      </Text>
                      <CDS.Text variant={CDS.Text.variant.bodySmall} color={CDS.Color.text.secondary}>Type Safe</CDS.Text>
                    </Box>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" color="purple.400">
                        4px
                      </Text>
                      <CDS.Text variant={CDS.Text.variant.bodySmall} color={CDS.Color.text.secondary}>Base Spacing</CDS.Text>
                    </Box>
                  </SimpleGrid>
                </CDS.Stack>
              </CardBody>
            </Card>
          </CDS.Stack>

          {/* Main Content Area */}
          <Card h="full" bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <CDS.Stack
                align={CDS.Stack.align.stretch}
                spacing={CDS.Spacing.large}
                h={CDS.Size.full}
              >
                {/* Section Header */}
                <Box>
                  <Heading size="md" mb={2} color={headingColor}>
                    {currentSection?.name}
                  </Heading>
                  <CDS.Text variant={CDS.Text.variant.bodyMedium} color={CDS.Color.text.secondary}>
                    {currentSection?.description}
                  </CDS.Text>
                </Box>

                <CDS.Divider color={CDS.Color.border.secondary} />

                {/* Dynamic Content */}
                <Box flex={1} overflow="auto">
                  {CurrentViewer && <CurrentViewer />}
                </Box>
              </CDS.Stack>
            </CardBody>
          </Card>
        </Grid>
      </Container>
    </Box>
  );
}

export default DesignSystemPage;
